import * as React from 'react';
import Editor, {
  Toolbar,
  BtnBold,
  BtnItalic,
  BtnUnderline,
  BtnBulletList,
  BtnNumberedList,
  BtnStyles,
} from 'react-simple-wysiwyg';
import styled from 'styled-components';

import { Dropdown } from '../Dropdown';
import { rem } from '../../tokens/spacing';

import {
  templateRichTextEditorRootStyles,
  templateRichTextInsertVariablesButtonStyles,
} from './TemplateRichTextEditor.styled';

const StyledEditorRoot = styled.div<{ $hasError: boolean }>`
  ${templateRichTextEditorRootStyles}
`;

const StyledVariablesWrap = styled.div`
  margin-top: 0;
`;

const StyledMenuScroll = styled.div`
  max-height: ${rem(280)};
  overflow-y: auto;
`;

const InsertVariablesTrigger = styled.button`
  ${templateRichTextInsertVariablesButtonStyles}
`;

export type TemplateVariableFormat = 'mustache' | 'bracket';

export interface TemplateRichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  hasError?: boolean;
  variables?: string[];
  variableFormat?: TemplateVariableFormat;
  semanticId?: string;
}

function formatVariable(name: string, format: TemplateVariableFormat): string {
  return format === 'bracket' ? `[${name}]` : `{{${name}}}`;
}

export const TemplateRichTextEditor = React.forwardRef<HTMLDivElement, TemplateRichTextEditorProps>(
  function TemplateRichTextEditor(
    {
      value,
      onChange,
      onBlur,
      disabled = false,
      hasError = false,
      variables = [],
      variableFormat = 'mustache',
      semanticId,
    },
    ref,
  ) {
    const editorRef = React.useRef<HTMLDivElement | null>(null);
    const savedSelectionRef = React.useRef<Range | null>(null);

    const saveSelection = React.useCallback(() => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
      }
    }, []);

    const insertText = React.useCallback(
      (textToInsert: string) => {
        const root = editorRef.current;
        if (!root) {
          onChange(`${value ?? ''}${textToInsert}`);
          return;
        }

        let selectionRange: Range | null = null;
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          if (root.contains(range.commonAncestorContainer)) {
            selectionRange = range;
          }
        }
        if (!selectionRange && savedSelectionRef.current) {
          selectionRange = savedSelectionRef.current;
          root.focus();
          const newSelection = window.getSelection();
          if (newSelection) {
            newSelection.removeAllRanges();
            newSelection.addRange(selectionRange);
          }
        }

        if (selectionRange) {
          selectionRange.deleteContents();
          selectionRange.insertNode(document.createTextNode(textToInsert));
          selectionRange.collapse(false);
          if (selection) {
            selection.removeAllRanges();
            selection.addRange(selectionRange);
          }
          onChange(root.innerHTML);
        } else {
          onChange(`${value ?? ''}${textToInsert}`);
        }
        saveSelection();
      },
      [onChange, saveSelection, value],
    );

    return (
      <StyledEditorRoot ref={ref} $hasError={hasError} data-semantic-id={semanticId}>
        <Editor
          ref={editorRef}
          value={value ?? ''}
          onChange={(e) => {
            onChange(e.target.value);
            saveSelection();
          }}
          onFocus={saveSelection}
          onBlur={() => {
            saveSelection();
            onBlur?.();
          }}
          disabled={disabled}
          containerProps={{
            className: 'editorClassName',
            style: { minHeight: rem(495), maxHeight: rem(495) },
          }}
        >
          <Toolbar>
            <BtnBold />
            <BtnItalic />
            <BtnUnderline />
            <BtnBulletList />
            <BtnNumberedList />
            <BtnStyles />
            {variables.length > 0 && (
              <StyledVariablesWrap className='toolbar-custom'>
                <Dropdown.Root align='start'>
                  <Dropdown.Trigger asChild>
                    <InsertVariablesTrigger type='button' disabled={disabled}>
                      <span className='material-symbols-outlined' aria-hidden>
                        add
                      </span>
                      Insert Variables
                    </InsertVariablesTrigger>
                  </Dropdown.Trigger>
                  <Dropdown.Content>
                    <StyledMenuScroll>
                      {variables.map((variableName) => (
                        <Dropdown.Item
                          key={variableName}
                          onSelect={() => {
                            setTimeout(() => {
                              insertText(formatVariable(variableName, variableFormat));
                            }, 0);
                          }}
                        >
                          {variableName}
                        </Dropdown.Item>
                      ))}
                    </StyledMenuScroll>
                  </Dropdown.Content>
                </Dropdown.Root>
              </StyledVariablesWrap>
            )}
          </Toolbar>
        </Editor>
      </StyledEditorRoot>
    );
  },
);

TemplateRichTextEditor.displayName = 'TemplateRichTextEditor';
